/**
 * ClienteFormSistema - Sub-component for ClienteFormModal
 * Renders: Alertas de Vencimento, Notificações, Intervalo de Agendamento (Epic 46)
 */
import React from 'react';
import { Calendar } from 'lucide-react';
import { ClienteInput } from '../services/clienteService';

interface ClienteFormSistemaProps {
  formData: ClienteInput;
  errors: Record<string, string>;
  loading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

const ClienteFormSistema: React.FC<ClienteFormSistemaProps> = ({
  formData,
  errors,
  loading,
  onChange,
}) => (
  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h3 className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2 uppercase tracking-wide">
      Sistema
    </h3>
    <div className="space-y-2">
      {/* Período de Segregação — ATENCAO: SEGREGA automaticamente */}
      <div className="bg-red-50 dark:bg-red-900/20 rounded p-2 border border-red-200 dark:border-red-800">
        <h4 className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">
          Período de Segregação (dias)
        </h4>
        <input
          type="number"
          name="periodo_segregacao"
          value={formData.periodo_segregacao ?? ''}
          onChange={onChange}
          min={0}
          max={365}
          placeholder="Ex: 60"
          className="w-full px-1.5 py-0.5 text-xs border border-red-300 dark:border-red-700 rounded focus:ring-1 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={loading}
        />
        <p className="text-[10px] text-red-700 dark:text-red-400 mt-1 leading-tight">
          ⚠️ SEGREGA automaticamente: produtos com validade ≤ este valor (dias) são retirados do saldo vendável.
        </p>
      </div>

      {/* Alertas de Vencimento — apenas visual/sonoro, NAO segrega */}
      <div>
        <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">
          Alertas de Vencimento (dias) — apenas visual/sonoro, NÃO segrega
        </h4>
        <div className="grid grid-cols-3 gap-1">
          {(['sinalizar_vencimento_1', 'sinalizar_vencimento_2', 'sinalizar_vencimento_3'] as const).map((field, idx) => (
            <div key={field}>
              <label className="block text-xs text-gray-500 mb-0.5">Nível {idx + 1}</label>
              <input
                type="number"
                name={field}
                value={formData[field] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  // Converter '' em null para permitir salvar campo vazio no backend
                  onChange({
                    target: {
                      name: field,
                      value: v === '' ? null : parseInt(v, 10),
                      type: 'number',
                    },
                  } as unknown as React.ChangeEvent<HTMLInputElement>);
                }}
                placeholder="Opcional"
                className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={loading}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1 mt-1">
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5" title="Opcional. Se vazio, usa Nível 1 como mínimo do range auditivo.">
              Override Mín (opcional)
            </label>
            <input
              type="number"
              name="intervalo_vencimento_min_dias"
              value={formData.intervalo_vencimento_min_dias || ''}
              onChange={onChange}
              placeholder="usa Nível 1"
              className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5" title="Opcional. Se vazio, usa Nível 2 como máximo do range auditivo.">
              Override Máx (opcional)
            </label>
            <input
              type="number"
              name="intervalo_vencimento_max_dias"
              value={formData.intervalo_vencimento_max_dias || ''}
              onChange={onChange}
              placeholder="usa Nível 2"
              className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={loading}
            />
          </div>
        </div>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 italic leading-tight">
          Por padrão, o sinal auditivo abaixo dispara entre <strong>Nível 1</strong> e <strong>Nível 2</strong>.
          Os campos Override são opcionais e substituem esse range.
        </p>
      </div>

      {/* Notificações — Sinal Auditivo (NÃO segrega) */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
          Notificações
        </h4>
        <label className="flex items-center gap-1.5 cursor-pointer mb-1">
          <input
            type="checkbox"
            name="sinalizacao_auditiva_ativa"
            checked={formData.sinalizacao_auditiva_ativa}
            onChange={onChange}
            className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
            disabled={loading}
          />
          <span className="text-xs text-gray-700 dark:text-gray-300">
            Sinalização auditiva entre Nível 1 e Nível 2
          </span>
        </label>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 italic mb-1 leading-tight">
          🔔 Toca som + exibe a mensagem abaixo no console de debug durante a leitura.
          Item é CONTADO NORMALMENTE (não é segregado, entra no saldo vendável).
        </p>
        <textarea
          name="mensagem_log_beebagem"
          value={formData.mensagem_log_beebagem}
          onChange={onChange}
          rows={2}
          className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Mensagem exibida no console do scanner (ex: Atenção: produto próximo do vencimento)"
          disabled={loading}
        />
      </div>

      {/* Epic 46 - Controle de Intervalo de Agendamento */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
          <Calendar className="w-3 h-3 inline mr-1" />
          Intervalo de Agendamento
        </h4>
        <div className="space-y-2">
          {/* Intervalo Mínimo */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                name="intervalo_agendamento_min_ativo"
                checked={formData.intervalo_agendamento_min_ativo}
                onChange={onChange}
                className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
                disabled={loading}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">Mín:</span>
            </label>
            <input
              type="number"
              name="intervalo_agendamento_min_dias"
              value={formData.intervalo_agendamento_min_dias || ''}
              onChange={onChange}
              min={1}
              className={`w-16 px-1.5 py-0.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.intervalo_agendamento_min_dias ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              } ${!formData.intervalo_agendamento_min_ativo ? 'opacity-50' : ''}`}
              placeholder="dias"
              disabled={loading || !formData.intervalo_agendamento_min_ativo}
            />
            <span className="text-xs text-gray-500">dias</span>
          </div>
          {errors.intervalo_agendamento_min_dias && (
            <p className="text-xs text-red-600">{errors.intervalo_agendamento_min_dias}</p>
          )}

          {/* Intervalo Máximo */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                name="intervalo_agendamento_max_ativo"
                checked={formData.intervalo_agendamento_max_ativo}
                onChange={onChange}
                className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
                disabled={loading}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">Máx:</span>
            </label>
            <input
              type="number"
              name="intervalo_agendamento_max_dias"
              value={formData.intervalo_agendamento_max_dias || ''}
              onChange={onChange}
              min={1}
              className={`w-16 px-1.5 py-0.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.intervalo_agendamento_max_dias ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              } ${!formData.intervalo_agendamento_max_ativo ? 'opacity-50' : ''}`}
              placeholder="dias"
              disabled={loading || !formData.intervalo_agendamento_max_ativo}
            />
            <span className="text-xs text-gray-500">dias</span>
          </div>
          {errors.intervalo_agendamento_max_dias && (
            <p className="text-xs text-red-600">{errors.intervalo_agendamento_max_dias}</p>
          )}

          <p className="text-xs text-gray-400 italic mt-1">
            Controla intervalo entre contagens agendadas
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default ClienteFormSistema;
